import React, { useRef } from 'react';
import VerifiedShieldBadge from '../common/VerifiedShieldBadge/VerifiedShieldBadge';

interface ChatUserAvatarProps {
    name: string;
    height?: number;
    width?: number;
    verified?: boolean;
    badgeSize?: 'sm' | 'md' | 'lg';
}

const AVATAR_COLORS = [
    { bg: '#e6ecff', fg: '#4294ff' },
    { bg: '#fce4ec', fg: '#e91e63' },
    { bg: '#e8f5e9', fg: '#4caf50' },
    { bg: '#fff3e0', fg: '#ff9800' },
    { bg: '#f3e5f5', fg: '#9c27b0' },
    { bg: '#e0f7fa', fg: '#00bcd4' },
    { bg: '#fbe9e7', fg: '#ff5722' },
    { bg: '#e8eaf6', fg: '#3f51b5' },
];

function getColorFromName(name: string) {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitial(name: string): string {
    if (!name) return '?';
    const display = name.includes('@') ? name.split('@')[0] : name;
    return display.charAt(0).toUpperCase();
}

// Auto-pick badge size from avatar size if not explicitly provided.
function autoBadgeSize(width: number): 'sm' | 'md' | 'lg' {
    if (width >= 64) return 'lg';
    if (width >= 36) return 'md';
    return 'sm';
}

const ChatUserAvatar: React.FC<ChatUserAvatarProps> = ({
    name,
    height = 36,
    width = 36,
    verified = false,
    badgeSize,
}) => {
    // Hooks must run unconditionally before any early return — keep
    // useRef at the top even though it's only consumed in the verified
    // branch below. (rules-of-hooks)
    const wrapperRef = useRef<HTMLSpanElement>(null);

    const resolvedBadgeSize = badgeSize || autoBadgeSize(width);
    const wrapperStyle: React.CSSProperties = {
        position: 'relative',
        display: 'inline-block',
        width,
        height,
        lineHeight: 0,
    };

    const avatarSvg = !name ? (
        <svg
            height={height}
            width={width}
            viewBox="0 0 64 64"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle cx="32" cy="32" fill="#e6ecff" r="31" />
            <path
                d="m56.877 50.4748a31.0647 31.0647 0 0 0 -49.7651-.0156 30.9669 30.9669 0 0 0 49.7651.0156z"
                fill="#4294ff"
            />
            <circle cx="32" cy="22" r="12" fill="#4294ff" />
        </svg>
    ) : (() => {
        const color = getColorFromName(name);
        const initial = getInitial(name);
        return (
            <svg
                height={height}
                width={width}
                viewBox="0 0 64 64"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle cx="32" cy="32" fill={color.bg} r="31" />
                <text
                    x="32"
                    y="32"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={color.fg}
                    fontSize="28"
                    fontFamily="Helvetica, Arial, sans-serif"
                    fontWeight="600"
                >
                    {initial}
                </text>
            </svg>
        );
    })();

    if (!verified) {
        return avatarSvg;
    }

    // In chat layout the shield badge is tiny (sm = 14px) and hard to
    // tap. Expand the trigger to the whole avatar: clicking anywhere on
    // the wrapper proxies to the badge's own click handler, which keeps
    // the tooltip state + portal anchored to the badge as usual. The
    // wrapper ref (declared at component top to satisfy rules-of-hooks)
    // is also passed to the badge so its click-outside listener treats
    // wrapper-internal mousedowns as "inside" and doesn't close+reopen
    // on each wrapper click (flicker).
    const onWrapperClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        const badge = e.currentTarget.querySelector(
            '.verified-shield-badge',
        ) as HTMLButtonElement | null;
        if (badge && !badge.contains(e.target as Node)) {
            badge.click();
        }
    };

    return (
        <span
            ref={wrapperRef}
            style={{ ...wrapperStyle, cursor: 'pointer' }}
            className="chat-user-avatar-wrapper"
            onClick={onWrapperClick}
        >
            {avatarSvg}
            <VerifiedShieldBadge size={resolvedBadgeSize} triggerRef={wrapperRef} />
        </span>
    );
};

export default ChatUserAvatar;
